import { getEventBus, EventBus, resetEventBus, setEventBus } from './EventBus';

export type EventDetail = {
    [key: string]: string|number|EventDetail
}

export type EventCallback<T extends EventDetail = EventDetail> = (args: T) => void;

type EventOptions = {
    bubbles: boolean
}

export type ModalConfirmationPayload = {
    title: string;
    body: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void;
    onCancel?: () => void;
}

export const AlertVariant = {
    Info: 'info',
    Success: 'success',
    Warning: 'warning',
    Danger: 'danger',
} as const;

export type AlertVariant = typeof AlertVariant[keyof typeof AlertVariant];

export const LoadingVariant = 'loading' as const;

export type LoadingVariant = typeof LoadingVariant;

export enum CustomEventNames {
    AccessDenied = 'access:denied',
    AlertDismiss = 'alert:dismiss',
    AlertHide = 'alert:hide',
    AlertShow = 'alert:show',
    ChangeLanguage = 'language:change',
    ChartLoaded = 'chart:loaded',
    FiltersApplied = 'filters:applied',
    FiltersDisabled = 'filters:disabled',
    FormIsDirty = 'subform:dirty',
    FormSubmit = 'form:submit',
    HeaderScrolled = 'header:scrolled',
    HeaderUnscrolled = 'header:unscrolled',
    InputChange = 'ui:input-modality',
    ItemDeleted = 'item:deleted',
    ItemSaved = 'item:saved',
    LoadingHide = 'loading:hide',
    LoadingShow = 'loading:show',
    ModalOpen = 'modal:open',
    ModalClose = 'modal:close',
    SidePanelClose = 'side-panel:close',
    SidePanelOpen = 'side-panel:open',
    SessionExpired = 'session:expired',
    SSOCheckStarted = 'sso:check-started',
    SSODone = 'sso:done',
    SSOLoginClick = 'sso:login-click',
    SSOLinkClick = 'sso:link-click',
    SSOLogout = 'sso:logout',
    SSOLogoutDone = 'sso:logout:done',
    SSOReturnRedirect = 'sso:return-redirect',
    SSOStart = 'sso:start',
    QueueCommandError = 'queue:command:error',
    QueueCommandSuccess = 'queue:command:success',
    QueueSettingCancel = 'queue:setting:cancel',
    QueueSettingConfirm = 'queue:setting:confirm',
    QueueTimerStart = 'queue:timer:start',
    QueueTimerStop = 'queue:timer:stop',
    UserMenuToggle = 'user-menu:toggle',
    VisibilityParentChanged = 'visibility:parent-changed',
    WishlistModalOpen = 'wishlist-modal:open',
}

const defaultOptions = {
    bubbles: true
};

/**
 * Dispatches an event via Global EventBus.
 */
export function dispatchEvent(
    eventName: CustomEventNames,
    detail?: EventDetail
): void {
    getEventBus().post(eventName, detail);
}

/**
 * Use dispatchDomEvent to facilitate custom event communication in a standard fashion
 * between unrelated controllers
 */
export const dispatchDomEvent = (
    element: HTMLElement,
    eventName: CustomEventNames,
    detail: EventDetail,
    options?: EventOptions = defaultOptions
): void => {
    const event = new CustomEvent(eventName, {
        ...options,
        detail,
    });
    element.dispatchEvent(event);
};

/**
 * Subscribes to a custom event via EventBus
 */
export function subscribeEvent<T extends EventDetail = EventDetail>(
    eventName: CustomEventNames,
    callback: EventCallback<T>
): void {
    getEventBus().subscribe(eventName, callback as EventCallback);
}

/**
 * Unsubscribes from a custom event via EventBus
 */
export function unsubscribeEvent<T extends EventDetail = EventDetail>(
    eventName: CustomEventNames,
    callback: EventCallback<T>
): void {
    getEventBus().unsubscribe(eventName, callback as EventCallback);
}

/**
 * Open a modal by ID and optionally inject title/content and a close callback.
 *
 * @param modalId   ID of the modal (should match data-modal-id-value)
 * @param options   Optional title/content and onClose callback
 */
export const openModal = (
    modalId: string,
    options: {
        title?: string;
        content?: string | HTMLElement;
        onClose?: () => void;
    } = {}
): void => {
    dispatchEvent(CustomEventNames.ModalOpen, {
        modalId,
        ...options
    });
};

/**
 * Open a modal by ID and inject title/content and a confirmation callback.
 *
 * onConfirm callback action is required and will be triggerd when the user clicks the confirm button.
 * onCancel callback is noop by default but can be provided if the caller needs to handle the cancel action.
 */
export const openModalWithConfirmation = (
    modalId: string,
    options: ModalConfirmationPayload
): void => {
    dispatchEvent(CustomEventNames.ModalOpen, {
        modalId,
        onCancel: () => {},
        ...options
    });
};

/**
 * Dispatch a close request for a modal by ID.
 *
 * @param modalId   ID of the modal to close
 */
export const closeModal = (modalId: string): void => {
    dispatchEvent(CustomEventNames.ModalClose, { modalId });
};

/**
 * Show a loading indicator.
 *
 * By default the UI is blocked (pointer-events disabled) until `loadingHide()` is called.
 * Pass `blocking: false` to show a non-blocking indicator — e.g. a background refresh where
 * the user should still be able to interact with the page.
 *
 * @example
 * loadingShow({ message: 'Saving…' });
 * loadingShow({ message: 'Refreshing', blocking: false });
 */
export const loadingShow = (options: {
    type?: LoadingVariant;
    message?: string;
    autoDismiss?: boolean;
    blocking?: boolean;
} = {}): void => {
    dispatchEvent(CustomEventNames.LoadingShow, options as EventDetail);
};

/**
 * Hide the loading indicator and release any UI block applied by `loadingShow()`.
 */
export const loadingHide = (): void => {
    dispatchEvent(CustomEventNames.LoadingHide);
};

export const alertShow = (options: {
    type?: AlertVariant;
    message: string;
    autoDismiss?: boolean;
}): void => {
    dispatchEvent(CustomEventNames.AlertShow, options as EventDetail);
};

export const alertHide = (): void => {
    dispatchEvent(CustomEventNames.AlertHide);
};

export { EventBus, resetEventBus, setEventBus, getEventBus };
